import type { GeneratedLevel } from '../model/level';
import type { InterruptedLevelState } from '../persistence/schema';
import { chooseHint } from '../solver/hint';
import { BoardIndex } from './boardIndex';

/**
 * The logical game session: rules, hearts, win/loss. No React, no PixiJS, no timers.
 *
 * Removal happens at one clearly defined stage — the moment a legal tap is accepted — so a
 * second tap on the same arrow can never remove it twice, however fast the taps arrive.
 * The renderer animates a sprite that no longer has any logical existence.
 */

export const STARTING_HEARTS = 3;

export type SessionStatus = 'ready' | 'won' | 'lost';

export type MoveResult =
  | {
      readonly kind: 'removed';
      readonly arrowId: number;
      readonly unlocked: readonly number[];
      readonly remaining: number;
      readonly won: boolean;
    }
  | {
      readonly kind: 'blocked';
      readonly arrowId: number;
      readonly blockerId: number;
      readonly heartsRemaining: number;
      readonly lost: boolean;
    }
  | {
      readonly kind: 'ignored';
      readonly reason: 'not-ready' | 'already-removed' | 'unknown-arrow';
    };

export interface SessionSnapshot {
  readonly status: SessionStatus;
  readonly heartsRemaining: number;
  readonly mistakes: number;
  readonly hintsUsed: number;
  readonly remainingArrows: number;
  readonly totalArrows: number;
  readonly removedArrowIds: readonly number[];
}

export class GameSession {
  readonly level: GeneratedLevel;

  private index: BoardIndex;
  private removed: number[] = [];
  private hearts = STARTING_HEARTS;
  private mistakeCount = 0;
  private hintCount = 0;
  private state: SessionStatus = 'ready';

  constructor(level: GeneratedLevel) {
    this.level = level;
    this.index = BoardIndex.fromBoard(level.board);
    if (this.index.aliveCount === 0) this.state = 'won';
  }

  get status(): SessionStatus {
    return this.state;
  }

  get heartsRemaining(): number {
    return this.hearts;
  }

  get mistakes(): number {
    return this.mistakeCount;
  }

  get hintsUsed(): number {
    return this.hintCount;
  }

  get remainingArrows(): number {
    return this.index.aliveCount;
  }

  get totalArrows(): number {
    return this.level.board.arrows.length;
  }

  get removedArrowIds(): readonly number[] {
    return this.removed;
  }

  isAlive(arrowId: number): boolean {
    return this.index.isAlive(arrowId);
  }

  isRemovable(arrowId: number): boolean {
    return this.index.isRemovable(arrowId);
  }

  nearestBlocker(arrowId: number): number {
    return this.index.nearestBlocker(arrowId);
  }

  removableIds(): number[] {
    return this.index.removableIds();
  }

  aliveIds(): number[] {
    return this.index.aliveIds();
  }

  snapshot(): SessionSnapshot {
    return {
      status: this.state,
      heartsRemaining: this.hearts,
      mistakes: this.mistakeCount,
      hintsUsed: this.hintCount,
      remainingArrows: this.index.aliveCount,
      totalArrows: this.totalArrows,
      removedArrowIds: [...this.removed],
    };
  }

  /** Applies a tap. The only mutating gameplay entry point. */
  tap(arrowId: number): MoveResult {
    if (this.state !== 'ready') return { kind: 'ignored', reason: 'not-ready' };
    if (!Number.isInteger(arrowId) || arrowId < 0 || arrowId >= this.totalArrows) {
      return { kind: 'ignored', reason: 'unknown-arrow' };
    }
    if (!this.index.isAlive(arrowId)) return { kind: 'ignored', reason: 'already-removed' };

    if (!this.index.isRemovable(arrowId)) {
      this.mistakeCount += 1;
      this.hearts -= 1;
      const lost = this.hearts <= 0;
      if (lost) this.state = 'lost';
      return {
        kind: 'blocked',
        arrowId,
        blockerId: this.index.nearestBlocker(arrowId),
        heartsRemaining: this.hearts,
        lost,
      };
    }

    const unlocked = this.index.remove(arrowId);
    this.removed.push(arrowId);
    const won = this.index.isCleared;
    if (won) this.state = 'won';
    return { kind: 'removed', arrowId, unlocked, remaining: this.index.aliveCount, won };
  }

  /**
   * Deterministic hint: the same board state and hint count always suggests the same arrow,
   * so a hint cannot be re-rolled by toggling it off and on.
   */
  requestHint(): number {
    if (this.state !== 'ready') return -1;
    const choice = chooseHint(
      this.level.rootSeed,
      this.index.aliveIds(),
      this.removableIds(),
      this.hintCount,
    );
    if (choice >= 0) this.hintCount += 1;
    return choice;
  }

  restart(): void {
    this.index = BoardIndex.fromBoard(this.level.board);
    this.removed = [];
    this.hearts = STARTING_HEARTS;
    this.mistakeCount = 0;
    this.hintCount = 0;
    this.state = this.index.aliveCount === 0 ? 'won' : 'ready';
  }

  /** Compact resume record; the board itself is regenerated from the level identity. */
  toInterruptedState(elapsedMs: number): InterruptedLevelState {
    return {
      identity: this.level.identity,
      boardHash: this.level.boardHash,
      removedArrowIds: [...this.removed],
      heartsRemaining: this.hearts,
      elapsedMs,
      hintsUsed: this.hintCount,
      mistakes: this.mistakeCount,
    };
  }

  /**
   * Rebuilds a session from a resume record. Returns null when the record does not match
   * the level, or when any recorded removal is not legal in sequence — a mismatch means the
   * record and the board disagree, and guessing would corrupt the player's progress.
   */
  static restore(level: GeneratedLevel, state: InterruptedLevelState): GameSession | null {
    if (state.boardHash !== level.boardHash) return null;

    const session = new GameSession(level);
    for (const id of state.removedArrowIds) {
      if (!session.index.isAlive(id) || !session.index.isRemovable(id)) return null;
      session.index.remove(id);
      session.removed.push(id);
    }
    session.hearts = Math.min(STARTING_HEARTS, Math.max(0, state.heartsRemaining));
    session.mistakeCount = state.mistakes;
    session.hintCount = state.hintsUsed;
    session.state = session.index.isCleared ? 'won' : session.hearts <= 0 ? 'lost' : 'ready';
    return session;
  }
}
